import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, Plus, Trash2, Printer, FileText, ChevronDown } from 'lucide-react';
import api from '../api';

export default function InvoiceModal({ isOpen, onClose, onCreated }) {
  const [step, setStep] = useState('create'); // create | preview
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [hondaVehicles, setHondaVehicles] = useState([]);
  const [catalogParts, setCatalogParts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [partSearch, setPartSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // Invoice data
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [vehicleInfo, setVehicleInfo] = useState('');
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      api.get('/catalog/honda/vehicles').then(r => setHondaVehicles(r.data)).catch(() => {});
      api.get('/catalog/honda/categories').then(r => setCategories(r.data)).catch(() => {});
    }
  }, [isOpen]);

  const fetchParts = useCallback(async () => {
    if (!selectedVehicle || !selectedYear) return;
    setLoading(true);
    try {
      const params = { vehicle: selectedVehicle, year: selectedYear };
      if (selectedCategory) params.category = selectedCategory;
      if (partSearch) params.search = partSearch;
      const { data } = await api.get('/catalog/honda', { params });
      setCatalogParts(data);
    } catch {} finally { setLoading(false); }
  }, [selectedVehicle, selectedYear, selectedCategory, partSearch]);

  useEffect(() => { fetchParts(); }, [fetchParts]);

  const handleSelectVehicle = (v, y) => {
    setSelectedVehicle(v);
    setSelectedYear(y);
    setVehicleInfo(`${y} ${v}`);
  };

  const addPart = (part) => {
    const existing = invoiceItems.find(i => i.oem_number === part.oem_number);
    if (existing) return;
    setInvoiceItems(prev => [...prev, {
      part_name: part.part_name,
      oem_number: part.oem_number,
      quantity: 1,
      unit_price: 0,
      condition: 'new_aftermarket'
    }]);
  };

  const updateItem = (index, field, value) => {
    setInvoiceItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const removeItem = (index) => {
    setInvoiceItems(prev => prev.filter((_, i) => i !== index));
  };

  const subtotal = invoiceItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal - discount + taxAmount;

  const handleSubmit = async () => {
    if (!customerName || invoiceItems.length === 0) return;
    setSaving(true);
    try {
      const { data } = await api.post('/admin/invoices', {
        customer_name: customerName, customer_email: customerEmail,
        customer_phone: customerPhone, customer_address: customerAddress,
        vehicle_info: vehicleInfo, items: invoiceItems,
        notes, tax_rate: taxRate, discount
      });
      setCreatedInvoice(data);
      setStep('preview');
      if (onCreated) onCreated(data);
    } catch {} finally { setSaving(false); }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('invoice-print-area');
    if (printContent) {
      const win = window.open('', '_blank');
      win.document.write(`<!DOCTYPE html><html><head><title>Invoice ${createdInvoice?.invoice_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #111; padding: 40px; font-size: 13px; }
          .inv-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; border-bottom: 3px solid #0F766E; padding-bottom: 20px; }
          .inv-logo { font-size: 28px; font-weight: 900; color: #F97316; letter-spacing: -1px; }
          .inv-logo-sub { font-size: 10px; font-weight: 700; color: #666; letter-spacing: 3px; text-transform: uppercase; }
          .inv-title { text-align: right; }
          .inv-title h1 { font-size: 32px; color: #0F766E; font-weight: 900; }
          .inv-title p { font-size: 12px; color: #666; margin-top: 4px; }
          .inv-meta { display: flex; justify-content: space-between; margin-bottom: 28px; }
          .inv-meta-box { width: 48%; }
          .inv-meta-box h3 { font-size: 11px; text-transform: uppercase; letter-spacing: 2px; color: #999; margin-bottom: 8px; font-weight: 700; }
          .inv-meta-box p { margin-bottom: 3px; font-size: 13px; }
          .inv-meta-box .val { font-weight: 600; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
          thead th { background: #0F766E; color: white; padding: 10px 12px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; font-weight: 700; }
          tbody td { padding: 10px 12px; border-bottom: 1px solid #eee; }
          tbody tr:nth-child(even) { background: #f9fafb; }
          .oem { font-family: monospace; font-size: 11px; color: #0F766E; }
          .text-right { text-align: right; }
          .totals { margin-left: auto; width: 280px; }
          .totals-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; }
          .totals-row.total { border-top: 2px solid #0F766E; padding-top: 10px; margin-top: 6px; font-size: 18px; font-weight: 900; color: #0F766E; }
          .inv-notes { margin-top: 32px; padding: 16px; background: #f3f4f6; border-radius: 6px; }
          .inv-notes h3 { font-size: 11px; text-transform: uppercase; letter-spacing: 2px; color: #999; margin-bottom: 6px; }
          .inv-footer { margin-top: 40px; text-align: center; color: #999; font-size: 11px; border-top: 1px solid #e5e7eb; padding-top: 16px; }
          @media print { body { padding: 20px; } }
        </style></head><body>${printContent.innerHTML}</body></html>`);
      win.document.close();
      win.print();
    }
  };

  const resetForm = () => {
    setStep('create'); setCustomerName(''); setCustomerEmail(''); setCustomerPhone('');
    setCustomerAddress(''); setVehicleInfo(''); setInvoiceItems([]); setNotes('');
    setTaxRate(0); setDiscount(0); setCreatedInvoice(null);
    setSelectedVehicle(''); setSelectedYear(''); setSelectedCategory(''); setPartSearch('');
  };

  if (!isOpen) return null;

  const veh = hondaVehicles.find(v => v.name === selectedVehicle);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="invoice-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-[#0F766E] rounded-t-lg">
          <div className="flex items-center gap-3">
            <FileText size={22} className="text-white" />
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Chivo', sans-serif" }}>
              {step === 'create' ? 'Create Invoice' : `Invoice ${createdInvoice?.invoice_number}`}
            </h2>
          </div>
          <button onClick={() => { resetForm(); onClose(); }} className="text-white/70 hover:text-white" data-testid="close-invoice-modal"><X size={22} /></button>
        </div>

        {step === 'create' ? (
          <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
            {/* Customer Info */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Customer Information</h3>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Customer Name *" value={customerName} onChange={e => setCustomerName(e.target.value)} className="border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-customer-name" />
                <input placeholder="Phone" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} className="border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-customer-phone" />
                <input placeholder="Email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} className="border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-customer-email" />
                <input placeholder="Address" value={customerAddress} onChange={e => setCustomerAddress(e.target.value)} className="border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-customer-address" />
              </div>
            </div>

            {/* Vehicle Selection */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Vehicle (Honda OEM Catalog)</h3>
              <div className="grid grid-cols-3 gap-3">
                <select value={selectedVehicle} onChange={e => { setSelectedVehicle(e.target.value); setSelectedYear(''); }} className="border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-vehicle-select">
                  <option value="">Select Vehicle</option>
                  {hondaVehicles.map(v => <option key={v.name} value={v.name}>{v.name}</option>)}
                </select>
                <select value={selectedYear} onChange={e => handleSelectVehicle(selectedVehicle, e.target.value)} disabled={!selectedVehicle} className="border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none disabled:opacity-40" data-testid="inv-year-select">
                  <option value="">Select Year</option>
                  {veh?.years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <input placeholder="Vehicle info (editable)" value={vehicleInfo} onChange={e => setVehicleInfo(e.target.value)} className="border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-vehicle-info" />
              </div>
            </div>

            {/* Parts Catalog */}
            {selectedVehicle && selectedYear && (
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">
                  OEM Parts Catalog — {selectedYear} {selectedVehicle} ({catalogParts.length} parts)
                </h3>
                <div className="flex gap-2 mb-3">
                  <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-3 text-gray-400" />
                    <input placeholder="Search by part name or OEM#..." value={partSearch} onChange={e => setPartSearch(e.target.value)} className="w-full border-2 border-gray-200 rounded pl-9 pr-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-part-search" />
                  </div>
                  <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-category-filter">
                    <option value="">All Categories</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="border rounded max-h-48 overflow-y-auto">
                  <table className="w-full text-xs" data-testid="inv-parts-table">
                    <thead><tr className="bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500 sticky top-0">
                      <th className="px-3 py-2">Part Name</th><th className="px-3 py-2">OEM #</th><th className="px-3 py-2">PartsLink</th><th className="px-3 py-2">Category</th><th className="px-3 py-2 w-16"></th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {catalogParts.map((part, i) => (
                        <tr key={i} className="hover:bg-[#0F766E]/5 cursor-pointer" data-testid={`catalog-part-${i}`}>
                          <td className="px-3 py-2 font-medium text-gray-900">{part.part_name}</td>
                          <td className="px-3 py-2 font-mono text-[#0F766E] font-bold">{part.oem_number}</td>
                          <td className="px-3 py-2 text-gray-500">{part.partslink}</td>
                          <td className="px-3 py-2 text-gray-500">{part.category}</td>
                          <td className="px-3 py-2">
                            <button onClick={() => addPart(part)} className="bg-[#F97316] text-white text-[10px] font-bold px-2 py-1 rounded hover:bg-[#EA580C] flex items-center gap-1" data-testid={`add-part-${i}`}>
                              <Plus size={10} /> Add
                            </button>
                          </td>
                        </tr>
                      ))}
                      {catalogParts.length === 0 && <tr><td colSpan="5" className="px-3 py-8 text-center text-gray-400">No parts found</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Invoice Items */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Invoice Items ({invoiceItems.length})</h3>
              {invoiceItems.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6 border-2 border-dashed border-gray-200 rounded">Select parts from the catalog above to add to invoice</p>
              ) : (
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm" data-testid="inv-items-table">
                    <thead><tr className="bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">
                      <th className="px-3 py-2">Part</th><th className="px-3 py-2">OEM #</th><th className="px-3 py-2 w-24">Condition</th><th className="px-3 py-2 w-16">Qty</th><th className="px-3 py-2 w-24">Unit Price</th><th className="px-3 py-2 w-24 text-right">Total</th><th className="px-3 py-2 w-10"></th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {invoiceItems.map((item, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 font-medium">{item.part_name}</td>
                          <td className="px-3 py-2 font-mono text-xs text-[#0F766E]">{item.oem_number}</td>
                          <td className="px-3 py-2"><select value={item.condition} onChange={e => updateItem(i, 'condition', e.target.value)} className="border rounded px-1 py-1 text-xs w-full"><option value="new_aftermarket">New</option><option value="used_oem">Used OEM</option><option value="refurbished">Refurb</option></select></td>
                          <td className="px-3 py-2"><input type="number" min="1" value={item.quantity} onChange={e => updateItem(i, 'quantity', parseInt(e.target.value) || 1)} className="border rounded px-2 py-1 w-14 text-xs text-center" /></td>
                          <td className="px-3 py-2"><input type="number" step="0.01" min="0" value={item.unit_price || ''} onChange={e => updateItem(i, 'unit_price', parseFloat(e.target.value) || 0)} placeholder="$0.00" className="border rounded px-2 py-1 w-20 text-xs" data-testid={`inv-price-${i}`} /></td>
                          <td className="px-3 py-2 text-right font-bold">${(item.unit_price * item.quantity).toFixed(2)}</td>
                          <td className="px-3 py-1"><button onClick={() => removeItem(i)} className="text-gray-400 hover:text-red-500"><Trash2 size={14} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Totals */}
            {invoiceItems.length > 0 && (
              <div className="flex justify-between items-start">
                <div className="w-1/2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1 block">Notes</label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Payment terms, special instructions..." className="w-full border-2 border-gray-200 rounded px-3 py-2 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="inv-notes" />
                </div>
                <div className="w-64 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="font-semibold">${subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-gray-500">Tax Rate</span>
                    <div className="flex items-center gap-1"><input type="number" step="0.1" min="0" max="20" value={taxRate} onChange={e => setTaxRate(parseFloat(e.target.value) || 0)} className="border rounded px-2 py-1 w-16 text-xs text-right" data-testid="inv-tax-rate" /><span className="text-xs">%</span></div>
                  </div>
                  {taxRate > 0 && <div className="flex justify-between"><span className="text-gray-500">Tax</span><span>${taxAmount.toFixed(2)}</span></div>}
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-gray-500">Discount</span>
                    <div className="flex items-center gap-1"><span className="text-xs">$</span><input type="number" step="0.01" min="0" value={discount} onChange={e => setDiscount(parseFloat(e.target.value) || 0)} className="border rounded px-2 py-1 w-20 text-xs text-right" data-testid="inv-discount" /></div>
                  </div>
                  <div className="flex justify-between border-t-2 border-[#0F766E] pt-2"><span className="font-bold text-gray-900">Total</span><span className="font-black text-xl text-[#0F766E]" data-testid="inv-total">${total.toFixed(2)}</span></div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <button onClick={() => { resetForm(); onClose(); }} className="px-6 py-2.5 border rounded text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleSubmit} disabled={saving || !customerName || invoiceItems.length === 0 || invoiceItems.some(i => !i.unit_price)}
                className="flex-1 bg-[#0F766E] hover:bg-[#0D5F59] text-white font-bold py-2.5 rounded transition-all active:scale-95 disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="submit-invoice-btn">
                <FileText size={16} /> {saving ? 'Creating...' : 'Create Invoice'}
              </button>
            </div>
          </div>
        ) : (
          /* PREVIEW / PRINT */
          <div className="p-6">
            <div className="flex gap-3 mb-6">
              <button onClick={handlePrint} className="bg-[#0F766E] text-white font-bold text-sm py-2.5 px-6 rounded flex items-center gap-2 hover:bg-[#0D5F59] active:scale-95" data-testid="print-invoice-btn">
                <Printer size={16} /> Print Invoice
              </button>
              <button onClick={() => { resetForm(); }} className="border-2 border-[#0F766E] text-[#0F766E] font-bold text-sm py-2.5 px-6 rounded hover:bg-[#0F766E]/5" data-testid="new-invoice-btn">
                <Plus size={16} className="inline mr-1" /> New Invoice
              </button>
            </div>

            <div id="invoice-print-area" className="bg-white border rounded p-8" data-testid="invoice-preview">
              <div className="inv-header flex justify-between items-start mb-8 border-b-[3px] border-[#0F766E] pb-5">
                <div>
                  <div className="text-3xl font-black text-[#F97316] tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>MARTINEZ</div>
                  <div className="text-[10px] font-bold text-gray-500 uppercase tracking-[3px]">Auto Parts</div>
                  <p className="text-xs text-gray-500 mt-2">740-564-8157 | mtzluis90@hotmail.com</p>
                </div>
                <div className="text-right">
                  <h1 className="text-3xl font-black text-[#0F766E]" style={{ fontFamily: "'Chivo', sans-serif" }}>INVOICE</h1>
                  <p className="text-sm text-gray-500 mt-1 font-mono">{createdInvoice?.invoice_number}</p>
                  <p className="text-xs text-gray-500 mt-1">Date: {new Date(createdInvoice?.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="flex justify-between mb-8">
                <div>
                  <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-gray-400 mb-2">Bill To</h3>
                  <p className="font-bold text-gray-900">{createdInvoice?.customer_name}</p>
                  {createdInvoice?.customer_phone && <p className="text-sm text-gray-600">{createdInvoice.customer_phone}</p>}
                  {createdInvoice?.customer_email && <p className="text-sm text-gray-600">{createdInvoice.customer_email}</p>}
                  {createdInvoice?.customer_address && <p className="text-sm text-gray-600">{createdInvoice.customer_address}</p>}
                </div>
                <div>
                  <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-gray-400 mb-2">Vehicle</h3>
                  <p className="font-bold text-gray-900">{createdInvoice?.vehicle_info || 'N/A'}</p>
                </div>
              </div>

              <table className="w-full border-collapse mb-6">
                <thead>
                  <tr className="bg-[#0F766E] text-white">
                    <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider">Part Description</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider">OEM Number</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider">Condition</th>
                    <th className="px-3 py-2.5 text-center text-[10px] font-bold uppercase tracking-wider">Qty</th>
                    <th className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider">Unit Price</th>
                    <th className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {createdInvoice?.items?.map((item, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2.5 text-sm font-medium text-gray-900">{item.part_name}</td>
                      <td className="px-3 py-2.5 text-sm font-mono text-[#0F766E] font-bold">{item.oem_number}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600">{item.condition === 'new_aftermarket' ? 'New' : item.condition === 'used_oem' ? 'Used OEM' : 'Refurb'}</td>
                      <td className="px-3 py-2.5 text-sm text-center">{item.quantity}</td>
                      <td className="px-3 py-2.5 text-sm text-right">${item.unit_price.toFixed(2)}</td>
                      <td className="px-3 py-2.5 text-sm text-right font-bold">${(item.unit_price * item.quantity).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex justify-end">
                <div className="w-64 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="font-semibold">${createdInvoice?.subtotal?.toFixed(2)}</span></div>
                  {createdInvoice?.tax_rate > 0 && <div className="flex justify-between"><span className="text-gray-500">Tax ({createdInvoice.tax_rate}%)</span><span>${createdInvoice.tax_amount?.toFixed(2)}</span></div>}
                  {createdInvoice?.discount > 0 && <div className="flex justify-between"><span className="text-gray-500">Discount</span><span>-${createdInvoice.discount?.toFixed(2)}</span></div>}
                  <div className="flex justify-between border-t-2 border-[#0F766E] pt-2 mt-2">
                    <span className="font-bold text-gray-900 text-base">Total</span>
                    <span className="font-black text-xl text-[#0F766E]">${createdInvoice?.total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {createdInvoice?.notes && (
                <div className="mt-8 p-4 bg-gray-50 rounded">
                  <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-gray-400 mb-1">Notes</h3>
                  <p className="text-sm text-gray-700">{createdInvoice.notes}</p>
                </div>
              )}

              <div className="mt-10 text-center text-gray-400 text-xs border-t pt-4">
                <p>Martinez Auto Parts LLC | 740-564-8157 | mtzluis90@hotmail.com</p>
                <p className="mt-1">Thank you for your business!</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
